import { createApp, type Component } from 'vue'
import { createPinia, type Pinia } from 'pinia'
import App from '~/App.vue'
import AppCheckoutReminder from '~/AppCheckoutReminder.vue'
import AppAffiliateLanding from '~/AppAffiliateLanding.vue'
import AppSteps from '~/AppSteps.vue'
import {
  useWidgetMode,
  type WidgetMode,
  type WidgetModeOpt,
} from './widgetMode.ts'
import i18n from '~/i18n'

export type RootWidgetMode = Exclude<WidgetMode, 'steps'>

export interface IWidgetModeHandler {
  target: Element
  stepsTarget?: string | Element
  domain: string
  allowStripe: boolean
}

type WidgetModeHandler = (context: IWidgetModeHandler) => void

const mountApp = ( component: Component, target: Element, widgetOptions: WidgetModeOpt, pinia: Pinia = createPinia()) => {
  const app = createApp(component)

  app.use(pinia)
  app.use(i18n)
  useWidgetMode.provide(widgetOptions, app)
  app.mount(target)
}

const resolveStepsTarget = ( searchTarget: Element ) => {
  const stepsTarget = searchTarget.getAttribute('data-steps-target')

  if (!stepsTarget) {
    const element = document.createElement('div')
    searchTarget.insertAdjacentElement('afterend', element)

    return element
  }

  const element = document.querySelector(stepsTarget)

  if (!element) {
    throw new Error(`Steps target not found: ${stepsTarget}`)
  }

  return element
}

const mountSearchMode: WidgetModeHandler = ({
  target,
  domain,
  allowStripe,
}) => {
  const instanceId = `shared-${Date.now()}`
  const pinia = createPinia()

  mountApp(
    App,
    target,
    { mode: 'search', instanceId, domain, allowStripe },
    pinia,
  )

  mountApp(
    AppSteps,
    resolveStepsTarget(target),
    { mode: 'steps', instanceId, domain, allowStripe },
    pinia,
  )
}

const mountCheckoutMode: WidgetModeHandler = ({
  target,
  domain,
  allowStripe,
}) => {
  mountApp(AppCheckoutReminder, target, {
    mode: 'checkout',
    domain,
    allowStripe,
  })
}

const mountAffiliateMode: WidgetModeHandler = ({
  target,
  domain,
  allowStripe,
}) => {
  mountApp(AppAffiliateLanding, target, {
    mode: 'affiliate',
    domain,
    allowStripe,
  })
}

const mountDefaultMode: WidgetModeHandler = ({ target, domain, allowStripe }) => {
  mountApp(App, target, { mode: 'default', domain, allowStripe })
}

const widgetModeHandlers: Record<RootWidgetMode, WidgetModeHandler> = {
  search: mountSearchMode,
  checkout: mountCheckoutMode,
  affiliate: mountAffiliateMode,
  default: mountDefaultMode,
}

export const mountWidgetMode = (
  mode: string,
  context: IWidgetModeHandler,
) => {
  const handler = widgetModeHandlers[mode as RootWidgetMode]

  if (!handler) {
    throw new Error(`Unsupported widget mode: ${mode}`)
  }

  handler(context)
}
